import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API from "./config/API";

function CreateAddRoomForm() {
  const [floors, setFloors] = useState([]);

  const [floorStatusMap, setFloorStatusMap] = useState({});

  const [rooms, setRooms] = useState([]);

  const [selectedFloor, setSelectedFloor] = useState("");

  const [errorMsg, setErrorMsg] = useState("");

  const [popup, setPopup] = useState("");

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));

  const emailId = user?.emailId || "";

  // ================= LOAD FLOORS =================
  useEffect(() => {
    loadFloors();
  }, []);

  // ================= LOAD FLOORS FUNCTION =================
  const loadFloors = () => {
    const user = JSON.parse(localStorage.getItem("user"));

    axios
      .get(`${API.V1}/floor_rooms/getFloors`, {
        params: {
          emailId,
        },
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        if (res.data.statusCode === 200) {
          setFloors(res.data.data.existedfloors || []);
          setFloorStatusMap(res.data.data.roomsCreatedAlreadyByThisFloor || {});
        } else {
          showPopup(res.data.message || "Failed to load floors", "error");
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          showPopup("Session Expired. Please login again.", "error");

          setTimeout(() => {
            navigate("/");
          }, 1000);

          return;
        }

        showPopup("Failed to load floors", "error");
      });
  };

  // ================= POPUP =================
  const showPopup = (msg) => {
    setPopup(msg);

    setTimeout(() => {
      setPopup("");
    }, 2500);
  };

  // ================= FETCH ROOMS =================
  const fetchRooms = async (floorName) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));

      const res = await axios.get(
        `${API.V1}/maxLimit/getFloorsRoomsNamesWithMaxCountDetails`,
        {
          params: {
            floorName,
            emailId,
          },
          headers: {
            Authorization: `Bearer ${user?.jwtToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      // ================= SUCCESS =================
      if (res.data.statusCode === 200) {
        const data = res.data.data.roomList || [];

        const formatted = data.map((r) => ({
          roomName: r.roomName,
          floorId: r.floorId,
          floorName: r.floorName,
          createdBy: emailId,
          isSnoringRoom: false,
          isLockerAvailable: false,
        }));

        setRooms(formatted);
        setErrorMsg("");
      }

      // ================= BACKEND VALIDATION =================
      else if (res.data.statusCode === 400) {
        setRooms([]);
        setErrorMsg(res.data.message);
        showPopup(res.data.message, "error");
      }
    } catch (error) {
      // Token expired
      if (error.response?.status === 401) {
        localStorage.clear();
        showPopup("Session Expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      // Backend validation
      if (error.response?.data) {
        const apiMessage =
          error.response.data.message || "Room creation exceeded";

        setRooms([]);
        setErrorMsg(apiMessage);
        showPopup(apiMessage, "error");
      } else {
        showPopup("Failed to load rooms", "error");
      }
    }
  };

  // ================= FLOOR CHANGE =================
  const handleFloorChange = (e) => {
    const floorId = e.target.value;

    setSelectedFloor(floorId);

    setRooms([]);

    setErrorMsg("");

    const floor = floors.find((f) => f.floorId == floorId);

    if (!floor) return;

    fetchRooms(floor.floorName);
  };

  // ================= DELETE ROOM =================
  const deleteRoom = (index) => {
    setRooms(rooms.filter((_, i) => i !== index));
  };

  const handleSnoringChange = (index, value) => {
    const updatedRooms = [...rooms];

    updatedRooms[index].isSnoringRoom = value === "true";

    setRooms(updatedRooms);
  };

  const handleLockerChange = (index, value) => {
    const updatedRooms = [...rooms];

    updatedRooms[index].isLockerAvailable = value === "true";

    setRooms(updatedRooms);
  };

  // ================= SAVE =================
  const handleSubmit = async () => {
    if (errorMsg) {
      return showPopup(errorMsg);
    }

    if (!selectedFloor) {
      return showPopup("Select floor");
    }

    if (rooms.length === 0) {
      return showPopup("No rooms available");
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      const res = await axios.post(`${API.V1}/floor_rooms/createRoom`, rooms, {
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data.statusCode === 200) {
        showPopup(res.data.message || "Rooms saved successfully", "success");

        setRooms([]);
        setSelectedFloor("");
        setErrorMsg("");

        // Reload floor status
        loadFloors();
      } else {
        showPopup(res.data.message || "Failed to save rooms", "error");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();
        showPopup("Session Expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      if (error.response?.data?.message) {
        showPopup(error.response.data.message, "error");
      } else {
        showPopup("Server Error", "error");
      }
    }
  };

  return (
    <div style={styles.container}>
      {/* ================= POPUP ================= */}
      {popup && <div style={styles.popup}>{popup}</div>}

      <div style={styles.card}>
        {/* ================= BACK BUTTON ================= */}
        <button
          style={styles.backBtn}
          onClick={() => navigate("/floorRoomsBeds")}
        >
          ← Back
        </button>

        {/* ================= TITLE ================= */}
        <h2 style={styles.title}>🏢 Floor Room Manager</h2>

        {/* ================= FLOOR SELECT ================= */}
        <select
          style={styles.select}
          value={selectedFloor}
          onChange={handleFloorChange}
        >
          <option value="">Select Floor</option>

          {floors.map((f) => {
            const status = floorStatusMap[f.floorName];

            const isExceeded = status === "Room Creation Exceed";

            return (
              <option
                key={f.floorId}
                value={f.floorId}
                style={{
                  color: isExceeded ? "red" : "black",

                  fontWeight: isExceeded ? "bold" : "normal",
                }}
              >
                {f.floorName}
                {" - "}
                {status}
              </option>
            );
          })}
        </select>

        {/* ================= ERROR ================= */}
        {errorMsg && <div style={styles.error}>{errorMsg}</div>}

        {/* ================= ROOM GRID ================= */}
        <div style={styles.grid}>
          {rooms.map((room, i) => (
            <div key={i} style={styles.roomBox}>
              <button style={styles.deleteBtn} onClick={() => deleteRoom(i)}>
                ✖
              </button>

              <h4 style={styles.roomTitle}>{room.roomName}</h4>

              {/* Snoring */}
              <label style={styles.label}>Is Snoring Room?</label>

              <select
                style={styles.roomSelect}
                value={room.isSnoringRoom.toString()}
                onChange={(e) => handleSnoringChange(i, e.target.value)}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>

              {/* Locker */}
              <label style={styles.label}>Is Locker Available?</label>

              <select
                style={styles.roomSelect}
                value={room.isLockerAvailable.toString()}
                onChange={(e) => handleLockerChange(i, e.target.value)}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          ))}
        </div>

        {/* ================= SAVE BUTTON ================= */}
        <button style={styles.saveBtn} onClick={handleSubmit}>
          💾 Save Rooms
        </button>
      </div>
    </div>
  );
}

export default CreateAddRoomForm;

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, #e0f2fe, #f8fafc)",
  },

  card: {
    width: "600px",
    minHeight: "500px",
    background: "#fff",
    padding: "30px",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    position: "relative",
  },

  title: {
    textAlign: "center",
    marginBottom: "20px",
    fontSize: "22px",
    fontWeight: "bold",
  },

  select: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    marginBottom: "10px",
  },

  error: {
    color: "#dc2626",
    fontWeight: "bold",
    marginBottom: "10px",
    textAlign: "center",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "15px",
    marginTop: "20px",
  },

  roomBox: {
    background: "#dbeafe",
    padding: "15px",
    borderRadius: "10px",
    textAlign: "center",
    fontWeight: "bold",
    color: "#1e40af",
    position: "relative",
  },

  deleteBtn: {
    position: "absolute",
    top: "5px",
    right: "6px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },

  saveBtn: {
    marginTop: "20px",
    width: "100%",
    padding: "14px",
    background: "#16a34a",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#111",
    color: "#fff",
    padding: "10px 15px",
    borderRadius: "6px",
    zIndex: 999,
  },

  backBtn: {
    position: "absolute",
    top: "15px",
    left: "15px",
    padding: "10px 14px",
    background: "#1e40af",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
  },

  roomTitle: {
    margin: "0 0 15px",
    color: "#1e40af",
    fontWeight: "bold",
    fontSize: "18px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#374151",
    fontSize: "14px",
  },

  roomSelect: {
    width: "100%",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "14px",
    background: "#fff",
  },
};
